import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
  useMemo,
  useEffect
} from 'react';
import { KeywordAssistApiResponse } from '../../types/keyword-assist.type';

/**
 * The state shape for Keyword Assist dashboard.
 */
export interface KeywordAssistState {
  keywordsData: KeywordAssistApiResponse | null;
  currentView: 'overview' | 'detailed';
  detailedViewURL: string;
}

/**
 * Actions supported by the Keyword Assist Reducer.
 */
export enum KeywordAssistActionTypes {
  SET_CURRENT_VIEW = 'SET_CURRENT_VIEW',
  SET_DETAILED_VIEW_URL = 'SET_DETAILED_VIEW_URL',
  SET_KEYWORDS_DATA = 'SET_KEYWORDS_DATA'
}

type KeywordAssistAction =
  | {
      type: KeywordAssistActionTypes.SET_CURRENT_VIEW;
      payload: 'overview' | 'detailed';
    }
  | { type: KeywordAssistActionTypes.SET_DETAILED_VIEW_URL; payload: string }
  | { type: KeywordAssistActionTypes.SET_KEYWORDS_DATA; payload: KeywordAssistApiResponse };


const initialState: KeywordAssistState = {
  keywordsData: null,
  currentView: 'overview',
  detailedViewURL: '/blog/squarespace-seo-tips'
};

const keywordAssistReducer = (
  state: KeywordAssistState,
  action: KeywordAssistAction
): KeywordAssistState => {
  switch (action.type) {
    case KeywordAssistActionTypes.SET_CURRENT_VIEW:
      return { ...state, currentView: action.payload };
    case KeywordAssistActionTypes.SET_DETAILED_VIEW_URL:
      return { ...state, detailedViewURL: action.payload };
    case KeywordAssistActionTypes.SET_KEYWORDS_DATA:
      return { ...state, keywordsData: action.payload };
    default:
      return state;
  }
};

/**
 * Context value interface including state and action setters.
 */
interface KeywordAssistContextValue {
  state: KeywordAssistState;
  changeCurrentView: (view: 'overview' | 'detailed') => void;
  setDetailedViewURL: (url: string) => void;
}

export const KeywordAssistContext = createContext<
  KeywordAssistContextValue | undefined
>(undefined);

interface KeywordAssistProviderProps {
  children: ReactNode;
  data: KeywordAssistApiResponse;
}

/**
 * `KeywordAssistProvider` wraps the Keyword Assist dashboard to supply global page state
 * and mutations using a clean, central useReducer pattern.
 */
export const KeywordAssistProvider = ({
  children,
  data
}: KeywordAssistProviderProps) => {
  const [state, dispatch] = useReducer(keywordAssistReducer, initialState);

  useEffect(() => {
    dispatch({
      type: KeywordAssistActionTypes.SET_KEYWORDS_DATA,
      payload: data
    });
  }, [data]);

  const changeCurrentView = useCallback((view: 'overview' | 'detailed') => {
    dispatch({
      type: KeywordAssistActionTypes.SET_CURRENT_VIEW,
      payload: view
    });
  }, []);

  const setDetailedViewURL = useCallback((url: string) => {
    dispatch({
      type: KeywordAssistActionTypes.SET_DETAILED_VIEW_URL,
      payload: url
    });
  }, []);

  const value = useMemo(
    () => ({
      state,
      changeCurrentView,
      setDetailedViewURL
    }),
    [state, changeCurrentView, setDetailedViewURL]
  );

  return (
    <KeywordAssistContext.Provider value={value}>
      {children}
    </KeywordAssistContext.Provider>
  );
};

/**
 * Hook to consume the KeywordAssistContext within provider children.
 */
export const useKeywordAssist = (): KeywordAssistContextValue => {
  const context = useContext(KeywordAssistContext);
  if (context === undefined) {
    throw new Error(
      'useKeywordAssist must be used within a KeywordAssistProvider'
    );
  }
  return context;
};
